import { AfterViewInit, Component, OnInit } from '@angular/core';
import { ColDef, ColumnApi, GridApi, GridOptions } from 'ag-grid-enterprise';
import { MessageService } from 'primeng/api';
import { ReportService } from '../..';
import 'ag-grid-enterprise';
import ChartDataLabels from 'chartjs-plugin-datalabels';

@Component({
    selector: 'app-future-occupancy-report',
    templateUrl: './future-occupancy-report.component.html',
    styleUrls: ['./future-occupancy-report.component.css'],
    providers: [MessageService],
    standalone: false
})
export class FutureOccupancyReportComponent implements OnInit, AfterViewInit {

  showSearchUI = true;
  formLoading  = false;
  activeTab    = 'overview';

  currentSearchForm: any;

  // search defaults
  defaultStartDate: Date;
  defaultEndDate: Date;

  // resTypeName → colour (used by pie chart slices AND legend dots)
  readonly typeColorMap: Record<string, string> = {
    'Owner':       'hotpink',
    'Owner Occ':   'hotpink',
    'Owner Guest': 'lightpink',
    'Guest':       'lightgrey',
    'Reserved':    'lightgrey',
    'Maintenance': 'darkgray',
    'Canceled':    'darkred',
    'Cancel':      'darkred',
    'Closed':      'green',
    'Postponed':   'lightblue',
    'Hold':        'yellow',
    'Open':        'lightgreen',
    'Expired':     'purple',
  };

  // Status code → background colour (matches res-sales-dashboard legend)
  readonly statusColorMap: Record<string, string> = {
    OO: 'hotpink',   OG: 'lightpink',  OP: 'lightgrey',
    NA: 'darkgray',  OX: 'red',        OC: 'green',
    PP: 'lightblue', QH: 'yellow',     QO: 'lightgreen',
    QE: 'purple',
  };
  // Status code → display label (aligned with pie chart labels)
  readonly statusLabelMap: Record<string, string> = {
    OO: 'Owner',       OG: 'Owner Guest', OP: 'Guest',
    NA: 'Maintenance', OX: 'Canceled',    OC: 'Closed',
    PP: 'Postponed',   QH: 'Hold',        QO: 'Open',
    QE: 'Expired',
  };

  // raw data from API
  reservations: any[] = [];
  monthlyData:    any[] = [];
  byTypeData:     any[] = [];
  byBedroomData:  any[] = [];

  // KPI
  totalReservations = 0;
  bookedNights      = 0;
  avgOccupancyPct   = 0;
  totalRevenue      = 0;

  // chart title (dynamic date range)
  chartDateRangeLabel = '';

  // bar chart — monthly occupancy %
  barChartData: any    = {};
  barChartOptions: any = {};

  // line chart — monthly revenue
  lineChartData: any    = {};
  lineChartOptions: any = {};

  // pie chart — occupancy share by type
  pieChartData: any    = {};
  pieChartOptions: any = {};
  piePlugins = [ChartDataLabels];

  // bedroom bar chart — check-ins by unit type
  bedroomChartData: any    = {};
  bedroomChartOptions: any = {};
  totalCheckIns            = 0;

  // daily check-ins / check-outs dual-line chart
  dailyChartData: any    = {};
  dailyChartOptions: any = {};

  // daily check-ins & check-outs broken down by bedroom type (multi-line)
  ciByBedroomChartData: any    = {};
  ciByBedroomChartOptions: any = {};
  coByBedroomChartData: any    = {};
  coByBedroomChartOptions: any = {};

  // reservations tab — multi-select filters (empty = show all)
  propertyFilters: string[] = [];
  resTypeFilters:  string[] = [];
  properties:      string[] = [];
  resTypeOptions = ['Owner', 'Owner Guest', 'Paid Owner Guest', 'Guest', 'Maintenance'];

  // ── ag-grid ────────────────────────────────────────────────────────────────
  gridApi: GridApi;
  columnApi: ColumnApi;

  defaultColDef: ColDef = {
    sortable: true,
    filter: true,
    resizable: true,
  };

  columnDefs: ColDef[] = [
    {
      headerName: 'Res. ID',      field: 'reservationId',      width: 90,  filter: 'agNumberColumnFilter',
      headerTooltip: 'Reservation ID — click to open the order in a new tab',
      cellRenderer: (params: any) => {
        if (params.node?.footer) return '<strong>TOTAL</strong>';
        const orderId = params.data?.orderId;
        if (!orderId) return params.value ?? '';
        return `<a href="/order/${orderId}" target="_blank" style="color:#4338ca;text-decoration:underline;cursor:pointer;">${params.value}</a>`;
      }
    },
    { headerName: 'Property',      field: 'propertyCode',        width: 110, headerTooltip: 'Short property code identifying the unit' },
    { headerName: 'Location',      field: 'locationName',        width: 180, headerTooltip: 'Full property / location name' },
    { headerName: 'Beds',          field: 'numBedrooms',         width: 70,  filter: 'agNumberColumnFilter', headerTooltip: 'Number of bedrooms in the unit' },
    { headerName: 'Baths',         field: 'numBathrooms',        width: 70,  filter: 'agNumberColumnFilter', headerTooltip: 'Number of bathrooms in the unit' },
    { headerName: 'Guest',         field: 'guestName',           width: 160, headerTooltip: 'Name of the primary guest or owner on the reservation' },
    { headerName: '#IN',           field: 'checkInDate',         width: 115, headerTooltip: 'Check-in date' },
    { headerName: '#OUT',          field: 'checkOutDate',        width: 115, headerTooltip: 'Check-out date' },
    {
      headerName: '< Days Prev',   field: 'daysSincePrev',       width: 110,
      headerTooltip: 'Days since the previous reservation checked out for this property (gap before this stay)',
      filter: 'agNumberColumnFilter',
      cellRenderer: (params: any) => {
        if (params.node?.footer) return '';
        return this.gapCellRenderer(params.value, '◀');
      }
    },
    {
      headerName: '> Days Next',   field: 'daysUntilNext',       width: 110,
      headerTooltip: 'Days until the next reservation checks in for this property (gap after this stay)',
      filter: 'agNumberColumnFilter',
      cellRenderer: (params: any) => {
        if (params.node?.footer) return '';
        return this.gapCellRenderer(params.value, '▶');
      }
    },
    {
      headerName: 'Num Days',      field: 'numNights',           width: 90,  filter: 'agNumberColumnFilter',
      headerTooltip: 'Total number of nights for the full reservation (not limited to search range)',
      aggFunc: 'sum',
    },
    {
      headerName: 'Nightly Rate',  field: 'nightlyRatePNT',      width: 125,
      headerTooltip: 'Average nightly rate = Total Rate ÷ total reservation nights',
      filter: 'agNumberColumnFilter',
      cellRenderer: (params: any) => params.node?.footer ? '' : this.currencyCellRenderer(params.value) + '/night'
    },
    {
      headerName: 'Total Rate',    field: 'revRoomRateNetTTL',   width: 115,
      headerTooltip: 'Total revenue for this reservation = Owner Amount (product 105) + KBM Management Fee (product 106)',
      filter: 'agNumberColumnFilter',
      aggFunc: 'sum',
      cellRenderer: (params: any) => `<span class="revenue-cell">${this.currencyCellRenderer(params.value)}</span>`
    },
    {
      headerName: 'Res. Type',     field: 'reservationStatus',   width: 145,
      headerTooltip: 'Reservation type: Owner (OO), Owner Guest (OG), Guest (OP), Maintenance (NA)',
      cellRenderer: (params: any) => {
        if (params.node?.footer) return '';
        return this.typeCellRenderer(params.value);
      }
    },
  ];

  gridOptions: GridOptions = {
    pagination: true,
    paginationPageSize: 100,
    animateRows: true,
    suppressAggFuncInHeader: true,
    tooltipShowDelay: 300,
    isExternalFilterPresent: () => this.propertyFilters.length > 0 || this.resTypeFilters.length > 0,
    doesExternalFilterPass: (node: any) => {
      const r = node.data;
      const propMatch = this.propertyFilters.length === 0 || this.propertyFilters.includes(r.propertyCode);
      const typeMatch = this.resTypeFilters.length  === 0 || this.resTypeFilters.includes(this.resolveTypeName(r.reservationStatus));
      return propMatch && typeMatch;
    },
  };

  // chart colours — matches mockup palette
  private chartColors = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#3b82f6'];

  canViewRevenue = false;

  constructor(
    public messageService: MessageService,
    public reportService: ReportService
  ) {
    const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
    const roles: number[] = (currentUser?.userRoles || []).map((r: any) => r.userRoleId);
    this.canViewRevenue = roles.includes(1000);
  }

  ngOnInit(): void {
    const today = new Date();
    this.defaultStartDate = new Date(today.getFullYear(), today.getMonth(), 1);
    this.defaultEndDate   = new Date(today.getFullYear(), today.getMonth() + 3, 0);

    this.gridOptions.defaultColDef          = this.defaultColDef;
    this.gridOptions.columnDefs             = this.columnDefs;
    this.gridOptions.rowData                = [];
    this.gridOptions.groupIncludeFooter     = true;
    this.gridOptions.groupIncludeTotalFooter = true;
  }

  ngAfterViewInit(): void {
    this.gridOptions.animateRows = true;
  }

  // ── ag-grid events ─────────────────────────────────────────────────────────

  onGridReady(grid: any): void {
    this.gridApi   = grid.api;
    this.columnApi = grid.columnApi;
  }

  onFirstDataRendered(): void {
    setTimeout(() => { this.columnApi?.autoSizeAllColumns(); }, 400);
  }

  /** Called when either dropdown changes */
  onFilterChange(): void {
    (this.gridOptions as any).api?.onFilterChanged();
  }

  // ── search ─────────────────────────────────────────────────────────────────

  onSearchSubmit(searchForm: any): void {
    this.currentSearchForm = searchForm;
    this.runReport(searchForm);
  }

  runReport(searchForm: any): void {
    try {
      this.formLoading = true;
      this.reportService.futureOccupancyReport(searchForm).subscribe(
        res => {
          const data = res.data.futureOccupancy;
          this.reservations  = data.reservations  || [];
          this.monthlyData   = data.monthlyData   || [];
          this.byTypeData    = data.byTypeData    || [];
          this.byBedroomData = data.byBedroomData || [];

          this.buildDateRangeLabel(searchForm);
          this.buildKpis();
          this.buildBarChart();
          this.buildLineChart();
          this.buildPieChart();
          this.buildBedroomChart();
          this.buildDailyChart(searchForm);
          this.buildDailyByBedroomCharts(searchForm);
          this.buildPropertyList();

          // push data into ag-grid and reset external filters
          this.propertyFilters = [];
          this.resTypeFilters  = [];
          (this.gridOptions as any).api?.setRowData(this.reservations);
          (this.gridOptions as any).api?.onFilterChanged();
          setTimeout(() => { this.columnApi?.autoSizeAllColumns(); }, 400);

          this.messageService.add({ severity: 'success', summary: 'Success', detail: res.message, life: 3000 });
        },
        err => {
          this.messageService.add({ severity: 'error', summary: 'Error', detail: err.message });
        },
        () => { this.formLoading = false; }
      );
    } catch (error) {
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Error running Future Occupancy Report' });
    }
  }

  // ── chart builders ─────────────────────────────────────────────────────────

  private buildDateRangeLabel(searchForm: any): void {
    const fmt = (d: any) => d ? new Date(d).toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' }) : '';
    if (searchForm?.startDate && searchForm?.endDate) {
      this.chartDateRangeLabel = `${fmt(searchForm.startDate)} – ${fmt(searchForm.endDate)}`;
    } else {
      this.chartDateRangeLabel = '';
    }
  }

  private buildKpis(): void {
    this.totalReservations = this.reservations.length;
    this.bookedNights      = this.reservations.reduce((s: number, r: any) => s + (r.clampedNights || 0), 0);
    this.totalRevenue      = this.monthlyData.reduce((s: number, m: any) => s + (m.revenue || 0), 0);
    const totalBooked    = this.monthlyData.reduce((s: number, m: any) => s + (m.bookedNights    || 0), 0);
    const totalAvailable = this.monthlyData.reduce((s: number, m: any) => s + (m.availableNights || 0), 0);
    this.avgOccupancyPct = totalAvailable > 0
      ? Math.round((totalBooked / totalAvailable) * 100)
      : 0;
  }

  private buildBarChart(): void {
    this.barChartData = {
      labels: this.monthlyData.map((m: any) => m.monthLabel),
      datasets: [{
        label: 'Occupancy %',
        backgroundColor: '#6366f1',
        borderRadius: 4,
        data: this.monthlyData.map((m: any) => m.occupancyPct)
      }]
    };
    this.barChartOptions = {
      plugins: { legend: { display: false } },
      scales: {
        x: { ticks: { autoSkip: false, maxRotation: 45, minRotation: 45 } },
        y: { min: 0, max: 100, ticks: { callback: (v: any) => v + '%' } }
      }
    };
  }

  private buildLineChart(): void {
    this.lineChartData = {
      labels: this.monthlyData.map((m: any) => m.monthLabel),
      datasets: [{
        label: 'Revenue',
        borderColor: '#10b981',
        backgroundColor: 'rgba(16,185,129,0.1)',
        borderWidth: 2.5,
        pointRadius: 5,
        fill: true,
        tension: 0.4,
        data: this.monthlyData.map((m: any) => m.revenue)
      }]
    };
    this.lineChartOptions = {
      plugins: { legend: { display: false } },
      scales: {
        x: { ticks: { autoSkip: false, maxRotation: 45, minRotation: 45 } },
        y: { ticks: { callback: (v: any) => '$' + (v / 1000).toFixed(0) + 'k' } }
      }
    };
  }

  private buildPieChart(): void {
    const bgColors = this.byTypeData.map((t: any) =>
      this.typeColorMap[t.resTypeName] || '#6366f1'
    );
    this.pieChartData = {
      labels: this.byTypeData.map((t: any) => t.resTypeName),
      datasets: [{ data: this.byTypeData.map((t: any) => t.numNights), backgroundColor: bgColors }]
    };
    this.pieChartOptions = {
      plugins: {
        legend: {
          display: false,
          position: 'bottom',
          labels: {
            usePointStyle: true,
            pointStyle: 'circle',
            padding: 14,
            font: { size: 12 },
            generateLabels: (chart: any) => {
              const ds = chart.data.datasets[0];
              return chart.data.labels.map((label: string, i: number) => ({
                text:        label,
                fillStyle:   ds.backgroundColor[i],
                strokeStyle: ds.backgroundColor[i],
                index:       i,
              }));
            }
          }
        },
        tooltip: { callbacks: { label: (ctx: any) => ` ${ctx.label}: ${ctx.raw} nights` } },
        datalabels: {
          color: '#fff',
          font: { weight: 'bold', size: 13 },
          formatter: (value: number, ctx: any) => {
            const total: number = (ctx.dataset.data as number[]).reduce((a, b) => a + b, 0);
            const pct = total > 0 ? Math.round((value / total) * 100) : 0;
            return pct > 0 ? pct + '%' : '';
          }
        }
      }
    };
  }

  private buildBedroomChart(): void {
    this.totalCheckIns = this.byBedroomData.reduce((s: number, b: any) => s + (b.numOccupied || 0), 0);

    this.bedroomChartData = {
      labels:   this.byBedroomData.map((b: any) => b.bedroomLabel),
      datasets: [{
        label: 'Check-ins',
        data:  this.byBedroomData.map((b: any) => b.numOccupied),
        backgroundColor: this.byBedroomData.map((b: any) => b.numOccupied === 0 ? '#e8eff1' : '#1a4a5a'),
        borderRadius: 6,
        borderSkipped: false
      }]
    };
    this.bedroomChartOptions = {
      plugins: {
        legend: { display: false },
        tooltip: { callbacks: { label: (ctx: any) => ` ${ctx.raw} check-in${ctx.raw !== 1 ? 's' : ''}` } }
      },
      scales: {
        x: { grid: { display: false }, ticks: { font: { size: 12 } } },
        y: { beginAtZero: true, ticks: { precision: 0, stepSize: 1 } }
      }
    };
  }

  private buildDailyChart(searchForm: any): void {
    const startDate = searchForm?.startDate ? new Date(searchForm.startDate) : null;
    const endDate   = searchForm?.endDate   ? new Date(searchForm.endDate)   : null;
    if (!startDate || !endDate || startDate > endDate) return;

    // build ordered date list
    const dates: string[] = [];
    const cur = new Date(startDate);
    while (cur <= endDate) {
      dates.push(cur.toISOString().slice(0, 10));
      cur.setDate(cur.getDate() + 1);
    }

    // count per day
    const inMap:  Record<string, number> = {};
    const outMap: Record<string, number> = {};
    dates.forEach(d => { inMap[d] = 0; outMap[d] = 0; });

    this.reservations.forEach((r: any) => {
      const ci = r.checkInDate  ? String(r.checkInDate).slice(0, 10)  : null;
      const co = r.checkOutDate ? String(r.checkOutDate).slice(0, 10) : null;
      if (ci && inMap[ci]   !== undefined) inMap[ci]++;
      if (co && outMap[co]  !== undefined) outMap[co]++;
    });

    // sparse x-axis labels — show at most ~12 labels regardless of range length
    const step = Math.ceil(dates.length / 12);
    const labels = dates.map((d, i) => {
      if (i % step !== 0 && i !== dates.length - 1) return '';
      const dt = new Date(d + 'T00:00:00');
      return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    });

    this.dailyChartData = {
      labels,
      datasets: [
        {
          label: 'Check-ins',
          borderColor: '#2563eb',
          pointBackgroundColor: '#fff',
          pointBorderColor: '#2563eb',
          pointBorderWidth: 2,
          pointRadius: 4,
          borderWidth: 2.5,
          fill: false,
          tension: 0.4,
          data: dates.map(d => inMap[d])
        },
        {
          label: 'Check-outs',
          borderColor: '#dc2626',
          pointBackgroundColor: '#fff',
          pointBorderColor: '#dc2626',
          pointBorderWidth: 2,
          pointRadius: 4,
          borderWidth: 2.5,
          fill: false,
          tension: 0.4,
          data: dates.map(d => outMap[d])
        }
      ]
    };

    this.dailyChartOptions = {
      interaction: {
        mode: 'index',
        intersect: false
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            title: (items: any[]) => {
              const idx = items[0]?.dataIndex;
              if (idx === undefined) return '';
              const dt = new Date(dates[idx] + 'T00:00:00');
              return dt.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
            },
            label: (ctx: any) => ` ${ctx.dataset.label}: ${ctx.raw}`
          }
        }
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: { maxRotation: 45, minRotation: 45 }
        },
        y: {
          beginAtZero: true,
          ticks: { precision: 0, stepSize: 1 }
        }
      }
    };
  }

  private buildDailyByBedroomCharts(searchForm: any): void {
    const startDate = searchForm?.startDate ? new Date(searchForm.startDate) : null;
    const endDate   = searchForm?.endDate   ? new Date(searchForm.endDate)   : null;
    if (!startDate || !endDate || startDate > endDate) return;

    const dates: string[] = [];
    const cur = new Date(startDate);
    while (cur <= endDate) { dates.push(cur.toISOString().slice(0, 10)); cur.setDate(cur.getDate() + 1); }

    const COLORS = ['#6366f1','#2563eb','#0891b2','#059669','#d97706','#dc2626','#9333ea','#db2777'];
    const bedroomLabel = (n: number) => n === 0 ? 'Studio' : `${n} Bed`;

    let maxBeds = 7;
    this.reservations.forEach((r: any) => { if (r.numBedrooms != null && r.numBedrooms > maxBeds) maxBeds = r.numBedrooms; });
    const unitTypes = Array.from({ length: maxBeds + 1 }, (_, i) => i);

    const ciMaps: Record<number, Record<string, number>> = {};
    const coMaps: Record<number, Record<string, number>> = {};
    unitTypes.forEach(b => {
      ciMaps[b] = {}; coMaps[b] = {};
      dates.forEach(d => { ciMaps[b][d] = 0; coMaps[b][d] = 0; });
    });

    this.reservations.forEach((r: any) => {
      const beds = r.numBedrooms ?? -1;
      if (!unitTypes.includes(beds)) return;
      const ci = r.checkInDate  ? String(r.checkInDate).slice(0, 10)  : null;
      const co = r.checkOutDate ? String(r.checkOutDate).slice(0, 10) : null;
      if (ci && ciMaps[beds][ci] !== undefined) ciMaps[beds][ci]++;
      if (co && coMaps[beds][co] !== undefined) coMaps[beds][co]++;
    });

    const step = Math.ceil(dates.length / 12);
    const labels = dates.map((d, i) => {
      if (i % step !== 0 && i !== dates.length - 1) return '';
      const dt = new Date(d + 'T00:00:00');
      return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    });

    const mkOptions = (title: string) => ({
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: { display: true, position: 'top', labels: { boxWidth: 12, font: { size: 11 } } },
        tooltip: {
          callbacks: {
            title: (items: any[]) => {
              const idx = items[0]?.dataIndex;
              if (idx === undefined) return '';
              const dt = new Date(dates[idx] + 'T00:00:00');
              return dt.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
            },
            footer: (items: any[]) => {
              const total = items.reduce((s: number, i: any) => s + (i.raw || 0), 0);
              return `Total: ${total}`;
            }
          }
        }
      },
      scales: {
        x: { grid: { display: false }, ticks: { maxRotation: 45, minRotation: 45 } },
        y: { beginAtZero: true, ticks: { precision: 0, stepSize: 1 } }
      }
    });

    const mkDatasets = (maps: Record<number, Record<string, number>>) =>
      unitTypes.map((b, i) => ({
        label: bedroomLabel(b),
        borderColor: COLORS[i % COLORS.length],
        pointBackgroundColor: '#fff',
        pointBorderColor: COLORS[i % COLORS.length],
        pointBorderWidth: 1.5,
        pointRadius: 3,
        borderWidth: 2,
        fill: false,
        tension: 0.4,
        data: dates.map(d => maps[b][d])
      }));

    this.ciByBedroomChartData    = { labels, datasets: mkDatasets(ciMaps) };
    this.coByBedroomChartData    = { labels, datasets: mkDatasets(coMaps) };
    this.ciByBedroomChartOptions = mkOptions('Check-ins by Unit Type');
    this.coByBedroomChartOptions = mkOptions('Check-outs by Unit Type');
  }

  private buildPropertyList(): void {
    const props = new Set<string>(this.reservations.map((r: any) => r.propertyCode).filter(Boolean));
    this.properties = Array.from(props);
  }

  // ── ag-grid cell renderers ─────────────────────────────────────────────────

  currencyCellRenderer(value: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency', currency: 'USD',
      minimumFractionDigits: 0, maximumFractionDigits: 0
    }).format(value || 0);
  }

  gapCellRenderer(days: number, arrow: string): string {
    const cls = days <= 1 ? 'gap-red' : days <= 3 ? 'gap-amber' : 'gap-gray';
    return `<span class="gap-badge ${cls}">${arrow} ${days || 0}d</span>`;
  }

  typeCellRenderer(status: string): string {
    const darkTextOn = new Set(['lightgreen', 'yellow', 'lightgrey', 'lightblue', 'lightpink']);
    const bg    = this.statusColorMap[status] || '#e5e7eb';
    const color = darkTextOn.has(bg) ? '#1f2937' : '#fff';
    const label = this.statusLabelMap[status] || status;
    return `<span style="background-color:${bg};color:${color};padding:2px 8px;border-radius:4px;font-size:0.8rem;font-weight:500;white-space:nowrap;">${label}</span>`;
  }

  // ── helpers ────────────────────────────────────────────────────────────────

  resolveTypeName(status: string): string {
    switch (status) {
      case 'OO': return 'Owner';
      case 'OG': return 'Owner Guest';
      case 'OP': return 'Guest';
      case 'NA': return 'Maintenance';
      default:   return status;
    }
  }
}
